import {
  FeatureFlag,
  FeatureFlagConditions,
  FeatureFlagEntry,
  FeatureFlagTreeNode,
} from '@/client/types/feature-flags';

/**
 * Managing feature flags requires `system.feature_flags.manage` server-side.
 * The client's `canManage` (which folds in isSuperAdmin) can disagree with the
 * backend, so writes/audit still 403 — return a clear message for that case,
 * or null so callers keep their own status-specific handling.
 */
export function featureFlagPermissionError(err: any): string | null {
  if (err?.status !== 403) return null;
  const desc: string | undefined =
    err?.data?.data?.description ?? err?.data?.description;
  return desc && /permission/i.test(desc)
    ? desc
    : 'Your account can view feature flags but cannot change them — this requires the system.feature_flags.manage permission.';
}

// ─── Tree building (hierarchical dot-pathed names, any-level veto) ─────────────

/**
 * Turn a flat flag list into a tree keyed by the dot segments of each name.
 * A structural ancestor with no flag of its own gets `flag: null`.
 * Children are sorted alphabetically; a node's own flag is attached at its path.
 */
export function buildFlagTree(flags: FeatureFlag[]): FeatureFlagTreeNode[] {
  const root: FeatureFlagTreeNode = { segment: '', path: '', flag: null, children: [] };

  const findOrCreate = (parent: FeatureFlagTreeNode, segment: string): FeatureFlagTreeNode => {
    let node = parent.children.find((c) => c.segment === segment);
    if (!node) {
      const path = parent.path ? `${parent.path}.${segment}` : segment;
      node = { segment, path, flag: null, children: [] };
      parent.children.push(node);
    }
    return node;
  };

  for (const flag of flags) {
    const segments = flag.name.split('.');
    let cursor = root;
    segments.forEach((seg) => {
      cursor = findOrCreate(cursor, seg);
    });
    cursor.flag = flag;
  }

  const sortRec = (node: FeatureFlagTreeNode) => {
    node.children.sort((a, b) => a.segment.localeCompare(b.segment));
    node.children.forEach(sortRec);
  };
  sortRec(root);

  return root.children;
}

/** Flatten a tree into a depth-annotated list, respecting a set of collapsed paths. */
export interface FlatNode {
  node: FeatureFlagTreeNode;
  depth: number;
  hasChildren: boolean;
}

export function flattenTree(
  nodes: FeatureFlagTreeNode[],
  collapsed: Set<string>,
  depth = 0,
): FlatNode[] {
  const out: FlatNode[] = [];
  for (const node of nodes) {
    const hasChildren = node.children.length > 0;
    out.push({ node, depth, hasChildren });
    if (hasChildren && !collapsed.has(node.path)) {
      out.push(...flattenTree(node.children, collapsed, depth + 1));
    }
  }
  return out;
}

/** All node paths that have children — used to expand/collapse-all. */
export function collectParentPaths(nodes: FeatureFlagTreeNode[]): string[] {
  const out: string[] = [];
  const walk = (n: FeatureFlagTreeNode[]) => {
    n.forEach((node) => {
      if (node.children.length) {
        out.push(node.path);
        walk(node.children);
      }
    });
  };
  walk(nodes);
  return out;
}

// ─── Conditions summarising ────────────────────────────────────────────────────

export const ELIGIBILITY_KEYS: (keyof FeatureFlagConditions)[] = ['region', 'roles', 'ip'];
export const ROLLOUT_KEYS: (keyof FeatureFlagConditions)[] = ['uids', 'orgs', 'percentage'];

export function isConditionsEmpty(conditions: FeatureFlagConditions | null | undefined): boolean {
  if (!conditions) return true;
  return !ELIGIBILITY_KEYS.concat(ROLLOUT_KEYS).some((k) => {
    const v = conditions[k];
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    return true; // percentage (number)
  });
}

/** Short human chips describing a conditions block, for the row summary. */
export function summarizeConditions(conditions: FeatureFlagConditions | null | undefined): string[] {
  if (isConditionsEmpty(conditions) || !conditions) return [];
  const chips: string[] = [];
  if (conditions.region?.length) chips.push(`region: ${conditions.region.join(', ')}`);
  if (conditions.roles?.length) chips.push(`roles: ${conditions.roles.join(', ')}`);
  if (conditions.ip?.length) chips.push(`ip: ${conditions.ip.length}`);
  if (conditions.uids?.length) chips.push(`uids: ${conditions.uids.length}`);
  if (conditions.orgs?.length) chips.push(`orgs: ${conditions.orgs.length}`);
  if (conditions.percentage != null) chips.push(`${conditions.percentage}% rollout`);
  return chips;
}

// ─── Entry helpers ─────────────────────────────────────────────────────────────

/**
 * Build the FULL entry to POST back. Because the override doc fully replaces the
 * effective entry, we always send enabled + description + conditions together.
 */
export function flagToEntry(flag: FeatureFlag, overrides: Partial<FeatureFlagEntry> = {}): FeatureFlagEntry {
  const entry: FeatureFlagEntry = {
    enabled: flag.enabled,
    description: flag.description ?? '',
    conditions: flag.conditions ?? null,
    ...overrides,
  };
  // Normalise an empty conditions object to null so we don't persist noise.
  if (isConditionsEmpty(entry.conditions)) entry.conditions = null;
  return entry;
}

// ─── Time formatting ───────────────────────────────────────────────────────────

/** Epoch seconds → short relative label ("3m ago", "2h ago", "just now"). */
export function relativeTimeFromSeconds(tsSeconds: number | null | undefined): string {
  if (tsSeconds == null) return '—';
  const nowMs = Date.now();
  const diffSec = Math.max(0, Math.floor(nowMs / 1000 - tsSeconds));
  if (diffSec < 45) return 'just now';
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/** Epoch seconds → absolute local timestamp for tooltips. */
export function absoluteTimeFromSeconds(tsSeconds: number | null | undefined): string {
  if (tsSeconds == null) return '';
  return new Date(tsSeconds * 1000).toLocaleString();
}
